<script>
    import { onMount } from "svelte";

    import { apiMethods } from "../../Services/index.svelte";

    import {
        decodeSearchParams,
        encodeSearchParams,
        getBooleanIcon,
        getText,
        goToURL,
        replaceURL,
        showError,
    } from "../../Utils/utilFunctions.svelte";

    import {
        allGroups,
        isMobile,
        loggedInDoctorDetail,
        user,
    } from "../../stores";

    import { fly } from "svelte/transition";

    import {
        Button,
        DoctorMultiSearchSelect,
        MultiSearchSelect,
        NotificationModal,
        PageLoading,
        ResetFilterLink,
        SvelteTable,
    } from "../../Components/Common/index.svelte";

    import TableFooter from "../../Components/Layout/Table/TableFooter.svelte";

    import { deleteIcon, editIcon } from "../../Utils/utilConstants.svelte";

    import { DirectUserRoles } from "../../Utils/utilEnums.svelte";

    import {
        cacheAllGroups,
        cachePreOpInstructions,
    } from "../../Utils/utilCache.svelte";
    import DefaultDoctorFilter from "../../Components/Common/DefaultDoctorFilter.svelte";

    export let params = {};

    let isGettingPreOpInstructions = false,
        returnedPreOpInstructions = [];

    let isDeletePreOpInstructionModalOpen = false,
        selectedPreOpInstruction,
        isDeletingPreOpInstruction = false;

    let filters = {},
        showFilters = false;

    let page = 0,
        pageSize = 25,
        count = 0;

    let searchGroupsArr = [],
        groupOptions = [];

    let selectedDoctors = [];

    $: isUserAdmin = $user.role == DirectUserRoles.Admin;
    $: isUserSupport = $user.role == DirectUserRoles.Support;
    $: isUserManager = $user.role == DirectUserRoles.Manager;
    $: isUserDoctor = $user.role == DirectUserRoles.Doctor;

    $: isUserEnableSearchGroup = isUserAdmin || isUserSupport;
    $: isUserEnableSearchDoctor = isUserEnableSearchGroup || isUserManager;

    $: columns = [
        {
            key: "id",
            title: "ID",
            headerClass: "text-left px-3 rounded-tl-xl",
            class: "text-left flex-1 px-3",
            renderValue: (v) => getText(v.id),
        },
        {
            key: "name",
            title: "Name",
            headerClass: "text-left px-3",
            class: "text-left flex-1 px-3",
            renderValue: (v) => getText(v.name),
        },
        {
            key: "groupName",
            title: "Group Name",
            headerClass: "text-left px-3",
            class: "text-left flex-1 px-3",
            renderValue: (v) => getText(v.groupName),
            isHidden: !isUserAdmin && !isUserSupport,
        },
        {
            key: "doctorName",
            title: "Doctor Name",
            headerClass: "text-left px-3",
            class: "text-left flex-1 px-3",
            renderValue: (v) => getText(v.accountName),
            isHidden: !isUserAdmin && !isUserSupport && !isUserManager,
        },
        {
            key: "attachToEstimatesEmail",
            title: "Attach to Estimates Email",
            headerClass: "text-left px-3",
            class: "text-left px-3",
            renderValue: (v) => getBooleanIcon(v.attachToEstimateEmail),
            csvContent: (v) => getBooleanIcon(v.attachToEstimateEmail, true),
        },
        {
            key: "addToEstimatesPdfs",
            title: "Add to Estimates PDFs",
            headerClass: "text-left px-3",
            class: "text-left px-3",
            renderValue: (v) => getBooleanIcon(v.addToEstimatePdf),
            csvContent: (v) => getBooleanIcon(v.addToEstimatePdf, true),
        },
        {
            key: "addToPortal",
            title: "Add to Portal",
            headerClass: "text-left px-3",
            class: "text-left px-3",
            renderValue: (v) => getBooleanIcon(v.addToPortal),
            csvContent: (v) => getBooleanIcon(v.addToPortal, true),
        },
        {
            key: "actions",
            title: "Actions",
            headerClass: "text-left px-3  rounded-tr-xl w-[2%]",
            renderValue: (v) => _renderActions(v),
            csvExportHidden: true,
        },
    ];

    const searchFilters = [
        {
            key: "preDefineDoctor",
            name: "Doctors",
            filterKey: "filter[doctorId]",
            separateHandle: true,
        },
        {
            key: "doctor",
            name: "Doctors",
            filterKey: "filter[doctorId]",
            separateHandle: true,
        },
        {
            key: "group",
            name: "Groups",
            filterKey: "filter[groupId]",
            separateHandle: true,
        },
    ];

    onMount(async () => {
        if (params.query) updateSearchParamsByQuery();

        await loadPreOpInstructions(
            page * pageSize,
            pageSize,
            _getSearchParams(),
        );

        if (isUserEnableSearchGroup) {
            await cacheAllGroups();

            groupOptions = $allGroups.map(({ id, name }) => ({
                id,
                type: name,
            }));
        }
    });

    const updateSearchParamsByQuery = () => {
        const arr = decodeSearchParams(params.query).split("&");

        if (arr.length) {
            for (let i = 0; i < arr.length; i++) {
                if (arr[i].includes("page"))
                    page = Number(arr[i].split("=")[1]);

                if (arr[i].includes("size"))
                    pageSize = Number(arr[i].split("=")[1]);

                searchFilters.forEach((sf) => {
                    if (arr[i].includes(sf.filterKey)) {
                        filters[sf.key] = arr[i].split("=")[1];
                        if (
                            arr[i].includes("groupId") &&
                            isUserEnableSearchGroup
                        ) {
                            searchGroupsArr = filters[sf.key]
                                .split(",")
                                .map((id) => Number(id));
                            filters[sf.filterKey] = searchGroupsArr.join(",");
                        }

                        if (
                            arr[i].includes("doctorId") &&
                            isUserEnableSearchDoctor
                        ) {
                            if (isNaN(arr[i].split("=")[1])) {
                                filters[sf.filterKey] = arr[i].split("=")[1];
                                selectedDoctors = filters[sf.filterKey]
                                    .split(",")
                                    .map((id) => Number(id));
                            } else {
                                filters[sf.filterKey] = Number(
                                    arr[i].split("=")[1],
                                );
                                selectedDoctors = [
                                    Number(filters[sf.filterKey]),
                                ];
                            }
                        }
                    }
                });
            }

            updateURL();
        }
    };

    const loadPreOpInstructions = async (start, limit, params) => {
        isGettingPreOpInstructions = true;

        await apiMethods.getMethod
            .getPreOpInstructions(start, limit, params)
            .then((res) => _handlePreOpInstructions(res))
            .catch((err) => showError(err));

        isGettingPreOpInstructions = false;
    };

    const createPreOpInstruction = () =>
        goToURL("/PreOpInstructions/create/new");

    const deletePreOpInstruction = async () => {
        isDeletingPreOpInstruction = true;

        await apiMethods.deleteMethod
            .deletePreOpInstruction(selectedPreOpInstruction)
            .then(async (res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            "Failed to delete selected PreOp Instruction, please refresh the page and try again and contact support if the issue persists.",
                    );
                else {
                    await loadPreOpInstructions(
                        page * pageSize,
                        pageSize,
                        _getSearchParams(),
                    );
                    await cachePreOpInstructions(true);
                }
            });

        isDeletingPreOpInstruction = false;

        _closeDeletePreOpModal();
    };

    const handleSelectDoctor = () => {
        filters.doctorId = selectedDoctors.join(",");

        handleFilterSelect();
    };

    const clearFilterDoctor = () => {
        selectedDoctors = [];

        delete filters.doctorId;

        handleFilterSelect();
    };

    const handleFilterSelect = () => {
        if (searchGroupsArr.length && isUserEnableSearchGroup)
            filters["filter[groupId]"] = searchGroupsArr.join(",");
        else delete filters["filter[groupId]"];

        if (selectedDoctors.length && isUserEnableSearchDoctor)
            filters["filter[doctorId]"] = selectedDoctors.join(",");
        else delete filters["filter[doctorId]"];

        page = 0;
        updateURL();
        loadPreOpInstructions(0, pageSize, _getSearchParams());
    };

    const updateURL = () =>
        replaceURL(
            `/PreOpInstructions/page=${page}&size=${pageSize}${_getParamsString()}`,
        );

    const toggleFilters = () => (showFilters = !showFilters);

    const filterTransition = (node, args) => ($isMobile ? fly(node, args) : "");

    const handleClickCell = ({ target: { className } }, rowData, key) => {
        if (key == "actions") {
            if (className.includes("edit"))
                goToURL(`/PreOpInstructions/view/${rowData.id}/isEdit=1`);
            else if (className.includes("delete"))
                _openDeletePreOpModal(rowData);
        } else goToURL(`/PreOpInstructions/view/${rowData.id}`);
    };

    const onPageChange = (e) => {
        page = Number(e.detail.page);

        updateURL();

        loadPreOpInstructions(
            Number(e.detail.firstDataIndex),
            pageSize,
            _getSearchParams(),
        );
    };

    const onPageSizeChange = (newPageSize) => {
        pageSize = newPageSize;
        page = 0;

        updateURL();

        loadPreOpInstructions(0, newPageSize, _getSearchParams());
    };

    // HELPER FUNCTIONS
    const _handlePreOpInstructions = (res) => {
        if (!res || res.error)
            showError(
                res.error ??
                    "Failed to load PreOp Instructions, please refresh the page and try again and contact support if the issue persists.",
            );
        else {
            returnedPreOpInstructions = [...res.results];
            count = res.total;
        }
    };

    const _renderActions = (data) => {
        const showActionIcons =
            isUserAdmin || isUserSupport
                ? true
                : isUserManager
                  ? data.groupId == $user.groupId
                  : isUserDoctor
                    ? data.accountId == $loggedInDoctorDetail.id
                    : false;

        return showActionIcons
            ? `
                <div class="flex flex-1 items-center justify-start gap-x-2">
                    <div class="editIcon tooltip tooltip-secondary tooltip-left cursor-pointer flex items-center justify-center w-[30px] h-[30px] hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Edit Pre-Op Instructions">
                        <i class="editIcon ${editIcon}"></i>
                    </div>    
                    <div class="deleteIcon tooltip tooltip-secondary tooltip-left cursor-pointer flex items-center justify-center w-[30px] h-[30px] hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Delete Pre-Op Instructions">
                        <i class="deleteIcon ${deleteIcon}"></i>
                    </div>    
                </div>
            `
            : "";
    };

    const _getSearchParams = () => {
        let params = {};

        if (filters["filter[groupId]"] && isUserEnableSearchGroup)
            params["filter[groupId]"] = filters["filter[groupId]"];

        if (filters["filter[doctorId]"] && isUserEnableSearchDoctor)
            params["filter[doctorId]"] = filters["filter[doctorId]"];

        return params;
    };

    const _getParamsString = () => {
        let str = "";
        const params = _getSearchParams();

        if (params)
            searchFilters.forEach((sf) => {
                if (params[sf.filterKey] || params[sf.filterKey] === 0)
                    str += `&${sf.filterKey}=` + params[sf.filterKey];
            });

        return encodeSearchParams(str);
    };

    const _openDeletePreOpModal = (data) => {
        isDeletePreOpInstructionModalOpen = true;
        selectedPreOpInstruction = { ...data };
    };

    const _closeDeletePreOpModal = () => {
        isDeletePreOpInstructionModalOpen = false;
        selectedPreOpInstruction = null;
    };
</script>

<div class="flex flex-col flex-1 gap-y-4">
    {#if $isMobile}
        <button
            class="btn btn-accent btn-sm w-28 flex flex-row gap-x-3"
            on:click={toggleFilters}
        >
            Filters
            <i
                style="font-size:20px"
                class={showFilters ? "fa arrow-up" : "fa arrow-down"}
                >&#xf104;
            </i>
        </button>
    {/if}
    {#if showFilters || !$isMobile}
        <div
            class="flex flex-0 {$isMobile
                ? 'flex-col gap-y-4  items-start'
                : 'justify-between items-end'}"
            in:filterTransition={{ duration: 200, y: -100 }}
        >
            <div class="flex gap-4 flex-wrap items-end">
                {#each searchFilters as searchFilter}
                    {#if searchFilter.key == "preDefineDoctor"}
                        {#if isUserEnableSearchDoctor}
                            <DefaultDoctorFilter
                                bind:filter={filters}
                                filterPropertyName={"filter[doctorId]"}
                                onChangeFunc={() => {
                                    selectedDoctors =
                                        filters["filter[doctorId]"].split(",");
                                    handleFilterSelect();
                                }}
                            />
                        {/if}
                    {:else if searchFilter.key == "doctor"}
                        {#if isUserEnableSearchDoctor}
                            <DoctorMultiSearchSelect
                                bind:searchResult={selectedDoctors}
                                onChangeFunc={handleSelectDoctor}
                                inputClass="w-32"
                                zIndex="3"
                            />
                        {/if}
                    {:else if searchFilter.key == "group"}
                        {#if isUserEnableSearchGroup}
                            <MultiSearchSelect
                                title={searchFilter.name}
                                bind:searchResult={searchGroupsArr}
                                optionsArr={groupOptions}
                                inputID="preOpInstructionsGroupSearchInput"
                                onChangeFunc={handleFilterSelect}
                                inputClass="w-32"
                            />
                        {/if}
                    {/if}
                {/each}

                <ResetFilterLink
                    bind:filter={filters}
                    onResetFilter={() => {
                        searchGroupsArr = [];
                        selectedDoctors = [];
                        handleFilterSelect();
                        clearFilterDoctor();
                    }}
                />
            </div>

            <Button clickFunc={createPreOpInstruction}>
                Create PreOp Instruction
            </Button>
        </div>
    {/if}

    <div class={$isMobile ? "h-auto w-minus48" : "relative flex-1"}>
        {#if isGettingPreOpInstructions}
            <PageLoading />
        {:else}
            <div
                class={$isMobile
                    ? "overflow-x-auto overflow-y-visible"
                    : "overflow-y-auto absolute inset-0"}
            >
                <SvelteTable
                    columns={columns.filter((col) => !col.isHidden)}
                    rows={returnedPreOpInstructions}
                    clickCellFunc={({ detail: { event, row, key } }) => {
                        handleClickCell(event, row, key);
                    }}
                    customRow="align-middle cursor-default"
                />
            </div>
        {/if}
    </div>

    <TableFooter
        {page}
        {pageSize}
        {count}
        tableData={returnedPreOpInstructions}
        {onPageChange}
        {onPageSizeChange}
        csvConfig={{
            columns: columns.filter((col) => !col.isHidden),
            data: returnedPreOpInstructions,
            fileName: "Pre-op Instructions",
            allDataApi: async () =>
                await apiMethods.getMethod.getPreOpInstructions(
                    0,
                    999999,
                    _getSearchParams(),
                ),
        }}
    />
</div>

<NotificationModal
    isModalOpen={isDeletePreOpInstructionModalOpen}
    title="Delete PreOp Instruction"
    closeModal={_closeDeletePreOpModal}
    closeModalFunction={_closeDeletePreOpModal}
    cancelText="Cancel"
    confirmAction={deletePreOpInstruction}
    submitText="Delete"
    isConfirming={isDeletingPreOpInstruction}
>
    <p class="leading-6 p-4">Delete selected PreOp Instruction?</p>
</NotificationModal>
